import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col lg:flex-row overflow-hidden pt-20 lg:pt-0"
    >
      {/* Background Logo Watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
        <img 
          src="/vritta-logo.png" 
          alt="" 
          className="w-[400px] lg:w-[800px] h-auto object-contain opacity-[0.06]"
        />
      </div>

      {/* Circuit/Chip Pattern Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `
              linear-gradient(rgba(16, 185, 129, 0.2) 1px, transparent 1px),
              linear-gradient(90deg, rgba(16, 185, 129, 0.2) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
          }}
        />
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(16, 185, 129, 0.4) 1px, transparent 0)`,
            backgroundSize: '20px 20px',
          }}
        />
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden flex flex-col items-center justify-center px-6 py-12 text-center z-20">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-xs">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-emerald-300">Agentic Workflows 2.0</span>
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-4xl sm:text-5xl font-bold font-outfit leading-tight mb-4"
        >
          <span className="text-white">.automate</span>
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-pink-400">
            Thinking
          </span>
        </motion.h1>

        {/* AI Image - Mobile */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 }}
          className="my-8 w-full max-w-[280px]"
        >
          <img
            src="https://images.unsplash.com/photo-1695902173528-0b15104c4554?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwaHVtYW4lMjBmYWNlJTIwd2lyZWZyYW1lJTIwbWVzaCUyMDNEJTIwYmx1ZSUyMGN5YW58ZW58MHx8fHwxNzcyMjUwMzcyfDA&ixlib=rb-4.1.0&q=85"
            alt="AI Visualization"
            className="w-full h-auto rounded-2xl"
          />
        </motion.div>

        {/* Description */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-slate-400 text-sm leading-relaxed mb-8 max-w-sm"
        >
          Vrittaa AI bridges the gap between static automation and autonomous agents. 
          Orchestrate complex workflows that reason, adapt, and integrate.
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="flex flex-col gap-3 w-full max-w-xs"
        >
          <Button
            data-testid="mobile-start-btn"
            className="w-full bg-white hover:bg-slate-100 text-slate-900 rounded-full py-6 text-sm font-semibold"
          >
            Start Building
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
          <Button
            data-testid="mobile-demo-btn"
            variant="outline"
            className="w-full rounded-full py-6 text-sm font-semibold border-slate-700 text-slate-300"
          >
            Watch Demo
          </Button>
        </motion.div>

        {/* Stats - Mobile */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="mt-10 grid grid-cols-3 gap-4 w-full max-w-xs"
        >
          <div className="text-center">
            <div className="text-xl font-bold text-white font-outfit">500+</div>
            <div className="text-xs text-slate-500">Agents</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-white font-outfit">10M+</div>
            <div className="text-xs text-slate-500">Workflows</div>
          </div>
          <div className="text-center">
            <div className="text-xl font-bold text-white font-outfit">99.9%</div>
            <div className="text-xs text-slate-500">Uptime</div>
          </div>
        </motion.div>
      </div>

      {/* Desktop Layout - Left AI Image */}
      <div className="hidden lg:flex w-1/2 items-center justify-center px-8 z-10">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, duration: 0.7 }}
          className="relative"
        >
          <div className="absolute -inset-4 bg-gradient-to-br from-emerald-500/20 via-cyan-500/10 to-pink-500/20 rounded-3xl blur-2xl" />
          <img
            src="https://images.unsplash.com/photo-1695902173528-0b15104c4554?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxkaWdpdGFsJTIwaHVtYW4lMjBmYWNlJTIwd2lyZWZyYW1lJTIwbWVzaCUyMDNEJTIwYmx1ZSUyMGN5YW58ZW58MHx8fHwxNzcyMjUwMzcyfDA&ixlib=rb-4.1.0&q=85"
            alt="AI Visualization"
            className="relative w-[400px] md:w-[500px] lg:w-[550px] h-auto object-cover rounded-2xl"
          />
        </motion.div>
      </div>

      {/* Desktop Layout - Right Content */}
      <motion.div
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5, duration: 0.6 }}
        className="hidden lg:flex w-1/2 flex-col justify-center pr-8 lg:pr-20 z-20"
      >
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-6"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-sm">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-emerald-300">Agentic Workflows 2.0</span>
          </span>
        </motion.div>

        {/* Main headline */}
        <div className="mb-8">
          <h1 className="text-5xl xl:text-7xl font-bold font-outfit leading-tight mb-4">
            <span className="text-white">.automate</span>
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-pink-400">
              Thinking
            </span>
          </h1>
        </div>

        {/* Description */}
        <div className="flex items-start gap-4 mb-10">
          <div className="w-12 h-px bg-slate-600 mt-3 flex-shrink-0" />
          <p className="text-slate-400 text-lg leading-relaxed max-w-lg">
            Vrittaa AI bridges the gap between static automation and autonomous agents. Orchestrate complex workflows that reason, adapt, and integrate across your entire stack.
          </p>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-wrap gap-4">
          <Button
            data-testid="start-building-btn"
            size="lg"
            className="bg-white hover:bg-slate-100 text-slate-900 rounded-full px-8 py-6 text-base font-semibold transition-all shadow-[0_0_30px_-5px_rgba(255,255,255,0.2)] hover:shadow-[0_0_40px_-5px_rgba(255,255,255,0.3)] group"
          >
            Start Building
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
          <Button
            data-testid="watch-demo-btn"
            size="lg"
            variant="outline"
            className="rounded-full px-8 py-6 text-base font-semibold border-slate-600 text-slate-300 hover:bg-slate-800/50 hover:text-white transition-all"
          >
            Watch Demo
          </Button>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.5 }}
          className="mt-12 flex gap-12"
        >
          <div>
            <div className="text-3xl font-bold text-white font-outfit">500+</div>
            <div className="text-sm text-slate-500">Active Agents</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-white font-outfit">10M+</div>
            <div className="text-sm text-slate-500">Workflows Automated</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-white font-outfit">99.9%</div>
            <div className="text-sm text-slate-500">Uptime SLA</div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
